import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { mediaAPI } from '../../../services/mediaAPI';

/**
 * Builder-embedded image/video picker over the existing Media Library
 * (GET /api/media) — no backend changes needed, this just reuses the same
 * tenant-scoped, validated upload/listing endpoints the standalone Media
 * page (frontend/src/pages/Media/Media.jsx) already uses.
 *
 * onSelect(url) is called with the picked (or freshly uploaded) media's
 * `url` field — the same value that ends up in node.props.src, which the
 * guest-facing LandingPageRenderer.jsx renders directly as an <img>/
 * <video> src, so whatever URL lands here must already be a real,
 * tenant-scoped, publicly-servable media URL — never a raw file/blob.
 */
// `tenantId` is the tenant of the page being edited. A superadmin has no
// tenant of their own, so it scopes both the listing and the upload to that
// page's tenant; for every other role the server ignores it and uses their own.
export default function MediaPickerModal({ open, mediaType = 'image', tenantId, onSelect, onClose }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const fileInputRef = useRef(null);
    const limit = 24;

    const fetchPage = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mediaAPI.getAll({ mediaType, page, limit, tenantId });
            setItems(res.data.data.media || []);
            setPagination(res.data.data.pagination || null);
        } catch (error) {
            toast.error('Failed to load media');
        } finally {
            setLoading(false);
        }
    }, [mediaType, page, tenantId]);

    useEffect(() => {
        if (open) fetchPage();
    }, [open, fetchPage]);

    // Reset to page 1 whenever the picker is reopened for a different field.
    useEffect(() => {
        if (open) setPage(1);
    }, [open]);

    async function handleUpload(e) {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        try {
            const res = await mediaAPI.upload(file, { tenantId });
            toast.success('Uploaded');
            // Select the freshly-uploaded file immediately — the common
            // case is "I don't have this image in the library yet."
            onSelect(res.data.data.url);
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    }

    if (!open) return null;

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-modern lb-media-picker" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <div><h2>Select {mediaType === 'video' ? 'Video' : 'Image'}</h2></div>
                    <button className="modal-close" onClick={onClose}><i className="fas fa-times"></i></button>
                </div>
                <div className="modal-body-modern">
                    <button className="btn-primary-modern lb-media-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                        <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                        {uploading ? 'Uploading...' : 'Upload New'}
                    </button>
                    <input ref={fileInputRef} type="file" hidden accept={mediaType === 'video' ? 'video/*' : 'image/*'} onChange={handleUpload} />

                    {loading ? (
                        <div className="loading-modern"><div className="spinner-modern"></div></div>
                    ) : items.length === 0 ? (
                        <div className="empty-state-modern">
                            <div className="empty-icon"><i className="fas fa-photo-video"></i></div>
                            <h3>No media yet</h3>
                            <p>Upload one above to get started.</p>
                        </div>
                    ) : (
                        <div className="lb-media-grid">
                            {items.map((item) => (
                                <button
                                    key={item._id}
                                    type="button"
                                    className="lb-media-item"
                                    onClick={() => { onSelect(item.url); onClose(); }}
                                    title={item.originalFilename}
                                >
                                    {item.mediaType === 'image' ? (
                                        <img src={item.thumbnailUrl || item.url} alt={item.altText || item.originalFilename} />
                                    ) : (
                                        <i className="fas fa-video fa-2x"></i>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {pagination && pagination.total > limit && (
                        <div className="lb-media-pagination">
                            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                            <span>Page {page} / {Math.max(1, Math.ceil(pagination.total / limit))}</span>
                            <button disabled={page >= Math.ceil(pagination.total / limit)} onClick={() => setPage((p) => p + 1)}>Next ›</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
